import { useEffect, useMemo, useState } from 'react';
import TextArea from '../../components/TextArea/TextArea';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import TemplateSideIndex from './TemplateSideIndex';

/** Lee el valor de un onChange que puede venir como evento (target.value) o valor crudo. */
const readVal = (e) => (e && typeof e === 'object' && 'target' in e ? e.target.value : e);

/**
 * Editor estructurado por sección. Cada sección es una tarjeta editable: título,
 * cuándo se usa (when), guía y contenido (HTML). Permite reordenar, agregar y eliminar.
 *
 * Reutiliza componentes del proyecto: Input (DS) para campos de una línea, TextArea (local,
 * autoGrow para ajustarse al contenido), ConfirmModal para confirmar el guardado. Resalta en
 * amarillo (warning) las secciones modificadas. Reporta el estado "sucio" al padre via
 * onDirtyChange para que éste pueda advertir antes de salir/cambiar de tab/versión.
 * Al guardar, llama onSave(updatedSectionsObject) → el backend crea una versión nueva activa.
 */
export default function TemplateSectionsEditor({ DesignSystemComponents, initialSections, t, saving, onSave, onDirtyChange }) {
  const { Button, Input, Modal } = DesignSystemComponents ?? {};

  // Copia editable (clon profundo del objeto inicial).
  const [doc, setDoc] = useState(() => JSON.parse(JSON.stringify(initialSections)));
  const [dirty, setDirty] = useState(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | success | error

  const sections = doc.sections || [];

  // Propaga el estado "sucio" al padre (para el guard de salida) — incl. al montar/desmontar.
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  // Mapa de las secciones originales (por key) para detectar qué cambió.
  const initialByKey = useMemo(() => {
    const m = new Map();
    (initialSections.sections || []).forEach((s) => m.set(s.key, JSON.stringify(s)));
    return m;
  }, [initialSections]);
  const isSectionChanged = (s) => {
    const init = initialByKey.get(s.key);
    return init === undefined ? true : init !== JSON.stringify(s);
  };
  const globalRulesChanged =
    JSON.stringify(doc.global_rules || []) !== JSON.stringify(initialSections.global_rules || []);

  const update = (next) => {
    setDoc(next);
    setDirty(true);
    if (saveState !== 'idle') setSaveState('idle');
  };

  const updateSection = (index, patch) => {
    update({ ...doc, sections: sections.map((s, i) => (i === index ? { ...s, ...patch } : s)) });
  };

  const move = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= sections.length) return;
    const reordered = sections.slice();
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    // Renumera order en decenas para reflejar el nuevo orden.
    reordered.forEach((s, i) => { s.order = (i + 1) * 10; });
    update({ ...doc, sections: reordered });
  };

  const removeSection = (index) => {
    update({ ...doc, sections: sections.filter((_, i) => i !== index) });
  };

  const addSection = () => {
    const order = (sections.length + 1) * 10;
    const key = `seccion_nueva_${sections.length + 1}_${order}`;
    update({
      ...doc,
      sections: [...sections, { key, title: t('tpl_sections_new'), order, when: null, when_hints: [], guidance: '', content: '', data_slots: [], evidence: null, examples: [] }],
    });
  };

  const updateGlobalRules = (text) => {
    update({ ...doc, global_rules: text.split('\n').map((l) => l.trim()).filter(Boolean) });
  };

  const doSave = async () => {
    setConfirmSaveOpen(false);
    setSaveState('saving');
    const ok = await onSave(doc);
    if (ok) {
      setDirty(false);
      setSaveState('success');
      setTimeout(() => setSaveState('idle'), 2500);
    } else {
      setSaveState('error');
    }
  };

  const ordered = sections.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const cardStyle = (changed) => ({
    border: `1px solid ${changed ? 'var(--warning-200, #ffe69c)' : '#E2E8F0'}`,
    borderRadius: 8,
    padding: 14,
    marginBottom: 10,
    scrollMarginTop: 12,
    background: changed ? 'var(--warning-100, #fff3cd)' : '#fff',
    boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
  });
  const changedChip = (
    <span style={{ fontSize: 10.5, fontFamily: 'sans-serif', color: '#92400E', background: 'var(--warning-200, #ffe69c)', borderRadius: 10, padding: '1px 8px' }}>
      {t('tpl_sections_changed')}
    </span>
  );

  return (
    <div className='row'>
      <div className='col-12 col-lg-9'>
        {/* Reglas globales */}
        <div style={cardStyle(globalRulesChanged)}>
          <div className='d-flex align-items-center gap-2 mb-2'>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#0F172A' }}>{t('tpl_sections_global_rules')}</span>
            {globalRulesChanged && changedChip}
          </div>
          <TextArea
            value={(doc.global_rules || []).join('\n')}
            onChange={(e) => updateGlobalRules(e.target.value)}
            rows={3}
            autoGrow
            disabled={saving}
          />
        </div>

        {ordered.map((s) => {
          const index = sections.indexOf(s);
          const conditional = !!s.when;
          const changed = isSectionChanged(s);
          return (
            <div id={`sec-${s.key}`} key={s.key} style={cardStyle(changed)}>
              <div className='d-flex justify-content-between align-items-start mb-3'>
                <div>
                  <div className='d-flex align-items-center gap-2'>
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#0F172A', lineHeight: 1.2 }}>
                      {s.title || t('tpl_sections_new')}
                    </span>
                    {changed && changedChip}
                  </div>
                  <code style={{ fontSize: 11, color: '#94A3B8' }}>[{s.key}]</code>
                </div>
                <div className='d-flex gap-1'>
                  <button type='button' className='btn btn-outline-secondary btn-sm py-0 px-2' onClick={() => move(index, -1)} title={t('tpl_sections_up')}>↑</button>
                  <button type='button' className='btn btn-outline-secondary btn-sm py-0 px-2' onClick={() => move(index, 1)} title={t('tpl_sections_down')}>↓</button>
                  <button type='button' className='btn btn-outline-danger btn-sm py-0 px-2' onClick={() => removeSection(index)} title={t('tpl_sections_remove')}>✕</button>
                </div>
              </div>

              {Input ? (
                <Input
                  label={t('tpl_field_title')}
                  value={s.title || ''}
                  onChange={(e) => updateSection(index, { title: readVal(e) })}
                  disabled={saving}
                  width='100%'
                />
              ) : (
                <input className='form-control form-control-sm mb-2' value={s.title || ''} onChange={(e) => updateSection(index, { title: e.target.value })} />
              )}

              <div className='form-check mt-2 mb-1'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id={`cond-${s.key}`}
                  checked={conditional}
                  disabled={saving}
                  onChange={(e) => updateSection(index, { when: e.target.checked ? (s.when || 'Usar cuando…') : null })}
                />
                <label className='form-check-label' htmlFor={`cond-${s.key}`} style={{ fontSize: 13 }}>
                  {t('tpl_section_conditional')}
                </label>
              </div>
              {conditional && (
                <TextArea
                  label={t('tpl_section_when')}
                  value={s.when || ''}
                  onChange={(e) => updateSection(index, { when: e.target.value })}
                  rows={2}
                  autoGrow
                  disabled={saving}
                />
              )}

              <TextArea
                label={t('tpl_field_guidance')}
                value={s.guidance || ''}
                onChange={(e) => updateSection(index, { guidance: e.target.value })}
                rows={2}
                autoGrow
                disabled={saving}
              />

              <TextArea
                label={t('tpl_field_content')}
                helper={t('tpl_field_content_helper')}
                value={s.content || ''}
                onChange={(e) => updateSection(index, { content: e.target.value })}
                rows={6}
                autoGrow
                disabled={saving}
              />

              {s.evidence && (
                <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>
                  {t('tpl_section_evidence', { present: s.evidence.present_in, total: s.evidence.total_docs })}
                </div>
              )}
            </div>
          );
        })}

        <div className='d-flex justify-content-between align-items-center mt-2'>
          {Button
            ? <Button label={t('tpl_sections_add')} size='sm' variant='naked' onClick={addSection} disabled={saving} />
            : <button type='button' className='btn btn-link btn-sm' onClick={addSection} disabled={saving}>{t('tpl_sections_add')}</button>}

          <div className='d-flex gap-2 align-items-center'>
            {/* Estado del guardado: spinner / check / cruz */}
            {saveState === 'saving' && (
              <span className='d-flex align-items-center gap-1' style={{ fontSize: 12, color: '#64748B' }}>
                <span className='spinner-border spinner-border-sm text-primary' role='status' aria-hidden='true' />
                {t('tpl_save_saving')}
              </span>
            )}
            {saveState === 'success' && (
              <span className='d-flex align-items-center gap-1' style={{ fontSize: 12, color: 'var(--success-500, #427328)' }}>✓ {t('tpl_save_done')}</span>
            )}
            {saveState === 'error' && (
              <span className='d-flex align-items-center gap-1' style={{ fontSize: 12, color: 'var(--error-500, #cc2836)' }}>✕ {t('tpl_save_error')}</span>
            )}
            {dirty && saveState === 'idle' && (
              <span style={{ fontSize: 12, color: '#B45309' }}>● {t('tpl_sections_unsaved')}</span>
            )}
            {Button
              ? <Button label={t('tpl_sections_save')} size='sm' variant='primary' onClick={() => setConfirmSaveOpen(true)} disabled={saving || !dirty || saveState === 'saving'} />
              : <button type='button' className='btn btn-primary btn-sm' onClick={() => setConfirmSaveOpen(true)} disabled={saving || !dirty || saveState === 'saving'}>{t('tpl_sections_save')}</button>}
          </div>
        </div>
      </div>

      {/* Índice de secciones (click → scroll; resalta las modificadas) */}
      <div className='col-12 col-lg-3'>
        <TemplateSideIndex
          items={ordered.map((s) => ({ id: `sec-${s.key}`, label: s.title, conditional: !!s.when, changed: isSectionChanged(s), title: s.when || '' }))}
        />
      </div>

      <ConfirmModal
        Modal={Modal}
        show={confirmSaveOpen}
        title={t('tpl_save_confirm_title')}
        message={t('tpl_save_confirm_message')}
        confirmLabel={t('tpl_sections_save')}
        cancelLabel={t('tpl_btn_cancel')}
        confirmVariant='primary'
        onConfirm={doSave}
        onClose={() => setConfirmSaveOpen(false)}
      />
    </div>
  );
}
